import { createSlice } from '@reduxjs/toolkit';
import { get, set } from 'lodash';
import { ReactNode } from 'react';
import uniqId from 'uniqid';
import { FloPagination } from '../../Pages/ManageFlos/ManageFlos.types';

export type GalleryImgType = {
	cloudFrontOriginalUrl: string;
	cloudFrontUrl: string;
	dataUrl?: string;
	id: string;
	extension?: string;
	name: string;
	fileSizeInBytes: string;
	uploadStatus?: string;
	meta?: {
		width: number;
		height: number;
	};
	category?: string;
	manuallySetFrameTime?: number;
	backgroundAnimationKey?: string;
	thumbnailUrl?: string;
	externalImageId?: string;
	fileSource?: string;
	doc360LanguageCode?: string;
	alternativeText?: string;
	aiAlternativeText?: string;
};

export type Doc360DriveSource = 'image_slide' | 'image_element';

export type Doc360DriveInsertedImage = {
	requestId: string;
	externalImageId: string;
	imageUrl?: string;
	imageName?: string;
	mimeType?: string;
	size?: number;
	width?: number;
	height?: number;
	langCode?: string;
	alternativeText?: string;
	aiAlternativeText?: string;
};

export type Category = {
	name: string;
	label: string;
	properties?: {
		[key: string]: string;
	};
};

export interface ImageGalleryReducerType {
	config: {
		visible: boolean;
		header: ReactNode;
		id?: string;
		confirmButtonText: string;
		dialogClassName?: string;
		variant?: string;
		confirmButtonType?: string;
		cancelButtonText: string;
		onCancel?(): boolean | void;
		onConfirm?(val: GalleryImgType, category: string): boolean | void;
		headerClassName?: string;
		classes?: {
			confirmButton?: string;
			cancelButton?: string;
			imagesList?: string;
			container?: string;
		};
		subHeading: string;
		recommendedResolution?: string;
		uploadButtonText: string;
		minDimensions?: {
			width: number;
			height: number;
		};
		showSelectFromOriginal?: boolean;
		showBackgroundSection?: boolean;
		showOnlyBackground?: boolean;
		initialAnimatedBackgroundKey?: string;
		videoDefaultTime?: number;
		doc360DriveSource?: Doc360DriveSource;
	};
	fileType: string;
	imagesList: {
		[category: string]: GalleryImgType[];
	};
	categories: Category[];
	pagination: FloPagination;
	errors: { [key: string]: boolean };
	doc360Drive: {
		source: Doc360DriveSource | null;
		reopenRequestId: number;
		insertedImage: Doc360DriveInsertedImage | null;
	};
}

const initialState = Object.freeze({
	imagesList: {
		uploaded: [],
	},
	config: {
		visible: false,
		header: 'Select image',
		id: '',
		onCancel: undefined,
		onConfirm: undefined,
		subHeading: 'GALLERY_SUBHEADING',
		cancelButtonText: 'GALLERY_CANCEL_BUTTON_TEXT',
		confirmButtonText: 'GALLERY_CONFIRM_BUTTON_TEXT',
		uploadButtonText: 'GALLERY_UPLOAD_BUTTON_TEXT',
	},
	fileType: 'image',
	pagination: {
		hasMore: false,
		pageNo: 1,
		pageSize: 30,
		totalRecords: 0,
	},
	errors: {},
	categories: [],
	doc360Drive: {
		source: null,
		reopenRequestId: 0,
		insertedImage: null,
	},
});

const imageGalleryReducers = {
	showFileGallery(
		state: ImageGalleryReducerType,
		{
			payload,
		}: { payload: Partial<ImageGalleryReducerType> & { fileType: 'image' | 'audio' } }
	) {
		return {
			...initialState,
			// subHeading: Translations.GALLERY_SUBHEADING,
			// cancelButtonText: Translations.GALLERY_CANCEL_BUTTON_TEXT,
			// confirmButtonText: Translations.GALLERY_CONFIRM_BUTTON_TEXT,
			// uploadButtonText: Translations.GALLERY_UPLOAD_BUTTON_TEXT,
			config: {
				...initialState.config,
				...payload,
				visible: true,
			},
			fileType: payload.fileType || initialState.fileType,
		};
	},
	hideFileGallery(state: ImageGalleryReducerType) {
		return {
			...initialState,
			// subHeading: Translations.GALLERY_SUBHEADING,
			// cancelButtonText: Translations.GALLERY_CANCEL_BUTTON_TEXT,
			// confirmButtonText: Translations.GALLERY_CONFIRM_BUTTON_TEXT,
			// uploadButtonText: Translations.GALLERY_UPLOAD_BUTTON_TEXT,
			doc360Drive: get(state, 'doc360Drive', initialState.doc360Drive),
		};
	},
	setDoc360DriveSource(
		state: ImageGalleryReducerType,
		{ payload }: { payload: { source: Doc360DriveSource } }
	) {
		return set(state, 'doc360Drive.source', payload.source);
	},
	doc360DriveBackClicked(state: ImageGalleryReducerType) {
		return set(
			state,
			'doc360Drive.reopenRequestId',
			get(state, 'doc360Drive.reopenRequestId', 0) + 1
		);
	},
	doc360DriveImageInserted(
		state: ImageGalleryReducerType,
		{ payload }: { payload: Doc360DriveInsertedImage }
	) {
		return set(state, 'doc360Drive.insertedImage', payload);
	},
	clearDoc360DriveInsertedImage(state: ImageGalleryReducerType) {
		return set(state, 'doc360Drive.insertedImage', null);
	},
	uploadGalleryFile(state: ImageGalleryReducerType, { payload }: { payload: any }) {
		return set(state, `imagesList.${payload.category}`, [
			{ uploadStatus: 'IN_PROGRESS', id: uniqId() },
			...get(state, `imagesList.${payload.category}`, []),
		]);
	},
	uploadGalleryFileError(state: ImageGalleryReducerType, { payload }: { payload: any }) {
		return set(
			state,
			`imagesList.${payload.category}`,
			get(state, `imagesList.${payload.category}`, []).slice(1)
		);
	},
	uploadGalleryFileSuccess(
		state: ImageGalleryReducerType,
		{ payload }: { payload: GalleryImgType }
	): ImageGalleryReducerType {
		state = set(state, `imagesList.${payload.category}`, [
			payload,
			...get(state, `imagesList.${payload.category}`, []).slice(1),
		]);
		state = set(
			state,
			'pagination.totalRecords',
			get(state, 'pagination.totalRecords') + 1
		);
		return state;
	},
	getGalleryFiles() {},
	getGalleryFilesSuccess(
		state: ImageGalleryReducerType,
		{
			payload,
		}: {
			payload: {
				data: GalleryImgType[];
				pageNo?: number;
				pagination: FloPagination;
				category: string;
			};
		}
	): ImageGalleryReducerType {
		state = set(state, 'pagination', payload.pagination);
		state = set(state, 'errors.getGalleryImages', false);
		const category = payload.category;
		if (payload.pageNo === 1) {
			return set(state, `imagesList.${category}`, payload.data);
		}
		return set(state, `imagesList.${category}`, [
			...get(state, `imagesList.${category}`, []),
			...payload.data,
		]);
	},
	getGalleryFilesError(
		state: ImageGalleryReducerType,
		{ payload }: { payload: { pageNo?: number } }
	): ImageGalleryReducerType {
		if (payload.pageNo === 1) {
			state = set(state, 'errors.getGalleryImages', true);
		}
		return state;
	},
	getGallerySystemFiles() {},
	getGallerySystemFilesSuccess(
		state: ImageGalleryReducerType,
		{
			payload,
		}: {
			payload: {
				data: GalleryImgType[];
				pageNo?: number;
				pagination: FloPagination;
				category: string;
			};
		}
	): ImageGalleryReducerType {
		state = set(state, 'pagination', payload.pagination);
		state = set(state, 'errors.getGalleryImages', false);
		const category = payload.category;
		if (payload.pageNo === 1) {
			return set(state, `imagesList.${category}`, payload.data);
		}
		return set(state, `imagesList.${category}`, [
			...get(state, `imagesList.${category}`, []),
			...payload.data,
		]);
	},
	getGallerySystemFilesError(
		state: ImageGalleryReducerType,
		{ payload }: { payload: { pageNo?: number } }
	): ImageGalleryReducerType {
		if (payload.pageNo === 1) {
			state = set(state, 'errors.getGalleryImages', true);
		}
		return state;
	},
	getDoc360RecentlyUsedImages() {},
	getDoc360RecentlyUsedImagesSuccess(
		state: ImageGalleryReducerType,
		{ payload }: { payload: { data: GalleryImgType[] } }
	): ImageGalleryReducerType {
		state = set(state, 'errors.getGalleryImages', false);
		state = set(state, 'imagesList.doc360RecentlyUsed', payload.data);
		return set(state, 'pagination', {
			hasMore: false,
			pageNo: 1,
			pageSize: 10,
			totalRecords: payload.data.length,
		});
	},
	getDoc360RecentlyUsedImagesError(state: ImageGalleryReducerType): ImageGalleryReducerType {
		return set(state, 'errors.getGalleryImages', true);
	},
	getGalleryCategories() {},
	getGalleryCategoriesSuccess(
		state: ImageGalleryReducerType,
		{
			payload,
		}: {
			payload: {
				data: Category[];
				fileType: string;
			};
		}
	): ImageGalleryReducerType {
		state = set(state, 'errors.getGalleryImages', false);
		state = set(state, 'categories', payload.data);
		payload.data.forEach((category) => {
			if (!state.imagesList[category.name]) {
				state = set(state, `imagesList.${category.name}`, []);
			}
		});
		state = set(state, 'categories', payload.data);
		return state;
	},
	getGalleryCategoriesError(
		state: ImageGalleryReducerType,
		{ payload }: { payload: { pageNo?: number } }
	): ImageGalleryReducerType {
		if (payload.pageNo === 1) {
			state = set(state, 'errors.getGalleryImages', true);
		}
		return state;
	},
};

export const imageGalleryReducer = createSlice({
	name: 'imageGallery',
	initialState,
	// @ts-ignore
	reducers: imageGalleryReducers,
});

export default imageGalleryReducer.reducer;

export const showFileGalleryAction = imageGalleryReducer.actions.showFileGallery;
export const hideFileGalleryAction = imageGalleryReducer.actions.hideFileGallery;
export const setDoc360DriveSourceAction = imageGalleryReducer.actions.setDoc360DriveSource;
export const doc360DriveBackClickedAction =
	imageGalleryReducer.actions.doc360DriveBackClicked;
export const doc360DriveImageInsertedAction =
	imageGalleryReducer.actions.doc360DriveImageInserted;
export const clearDoc360DriveInsertedImageAction =
	imageGalleryReducer.actions.clearDoc360DriveInsertedImage;
export const uploadGalleryFileAction = imageGalleryReducer.actions.uploadGalleryFile;
export const uploadGalleryFileErrorAction =
	imageGalleryReducer.actions.uploadGalleryFileError;
export const uploadGalleryFileSuccessAction =
	imageGalleryReducer.actions.uploadGalleryFileSuccess;
export const getGalleryFilesSuccessAction =
	imageGalleryReducer.actions.getGalleryFilesSuccess;
export const getGalleryFilesErrorAction =
	imageGalleryReducer.actions.getGalleryFilesError;
export const getGalleryFilesAction = imageGalleryReducer.actions.getGalleryFiles;
export const getGallerySystemFilesAction =
	imageGalleryReducer.actions.getGallerySystemFiles;
export const getGallerySystemFilesSuccessAction =
	imageGalleryReducer.actions.getGallerySystemFilesSuccess;
export const getGallerySystemFilesErrorAction =
	imageGalleryReducer.actions.getGallerySystemFilesError;
export const getDoc360RecentlyUsedImagesAction =
	imageGalleryReducer.actions.getDoc360RecentlyUsedImages;
export const getDoc360RecentlyUsedImagesSuccessAction =
	imageGalleryReducer.actions.getDoc360RecentlyUsedImagesSuccess;
export const getDoc360RecentlyUsedImagesErrorAction =
	imageGalleryReducer.actions.getDoc360RecentlyUsedImagesError;
export const getGalleryCategoriesAction =
	imageGalleryReducer.actions.getGalleryCategories;
export const getGalleryCategoriesSuccessAction =
	imageGalleryReducer.actions.getGalleryCategoriesSuccess;
export const getGalleryCategoriesErrorAction =
	imageGalleryReducer.actions.getGalleryCategoriesError;
